import { DateUtils } from '@/core/utils/date.utils';
import { getScreenHeightWithoutTopBar } from '@/core/utils/screen-height';
import { Company } from '@/models/company';
import { FormatType, FormatTypeApi, FormatTypeValue } from '@/models/format-type';
import { Gen0004 } from '@/models/general-information/gen-0004.collaborator';
import { SecurityUser } from '@/models/security/security-user';
import { AppFilters } from '@/store/filters/store.filters';
import { appStoreFiltersIS } from '@/store/filters/store.filters.state';
import { AppGlobal } from '@/store/global/store.global';
import { appStoreGlobalActions } from '@/store/global/store.global.action';
import { appStoreGlobalIS } from '@/store/global/store.global.state';
import { AppScreen } from '@/store/screen/store.screen';
import { appStoreScreenIS } from '@/store/screen/store.screen.state';
import { AppSettings } from '@/store/settings/store.settings';
import { appStoreSettingsIS } from '@/store/settings/store.settings.state';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { debounceTime, of, Subscription, tap } from 'rxjs';
import { BaseService } from './base.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class BaseComponent implements OnDestroy, OnInit {
  subscriptions: { [key: string]: Subscription } = {};
  private _isLoading: boolean = false;
  private _isSaving: boolean = false;
  private _isInnerLoading: boolean = false;

  global: AppGlobal = appStoreGlobalIS;
  settings: AppSettings = appStoreSettingsIS;
  filters: AppFilters = appStoreFiltersIS;
  screen: AppScreen = appStoreScreenIS;

  user!: SecurityUser;
  collaborator!: Gen0004;
  company!: Company;
  isSalesforce = true;

  /**
   * @deprecated Create getter to obtain this from the collaborator object, use collabCode or collabDocument
   */
  agentDocument?: string;
  /**
   * @deprecated Create getter to obtain this from the collaborator object, use collabRole
   */
  agentRole?: string;
  dateSelected? = new Date();

  formatTypeList: FormatType[] = [];
  formatTypeOption?: FormatType;
  formatTypeSelected: FormatTypeValue = 'fixedPoint';
  formatTypeApi: FormatTypeApi = 'units';

  isWeb = false;
  isSmall = false;
  isPortrait = false;
  isMedium = false;
  isLarge = false;

  textExp = `<span class="text-xs vertical-align-top" style="vertical-align: top;">-1</span>`;
  valueIcon = `<i class="fa-fw fa-solid fa-dollar-sign"></i>`;
  p360Icon = `<i class="p360__icon u-icon"></i>`;

  innerHeight: number = 0;
  innerWidth: number = 0;
  screenHeight: number = 0;
  screenWidth: number = 0;

  connected = true;

  shouldScrollToTop = false;

  @Input() currentDate!: Date;
  endCurrentDate?: Date;
  nextDate?: Date;

  showGpsError = false;
  isWatchingPosition = true;
  currentPosition: { latitude?: number; longitude?: number } = {
    latitude: 0,
    longitude: 0,
  };

  private gpsTries = 0;
  private timeoutId?: any;

  protected ignoreClaims?: boolean = true;

  protected service = inject(BaseService);
  protected platform = inject(Platform);
  protected store = inject(Store<any>);

  constructor() {
    this.subscriptions = {};
  }

  ngOnInit(): void {
    if (this.shouldScrollToTop) {
      document.querySelector('.layout-content')?.scrollTo({ top: 0 });
    }

    if (!this.currentDate) {
      this.currentDate = new Date();
      this.currentDate.setHours(0, 0, 0, 0);
    }
    this.endCurrentDate = new Date(this.currentDate);
    this.endCurrentDate.setHours(23, 59, 59, 999);
    this.nextDate = new Date(this.currentDate);
    DateUtils.addDate(this.nextDate, 1);

    of('').pipe(debounceTime(100), tap(this.handleOnResize)).subscribe();
    this.platform.resize.pipe(debounceTime(100), tap(this.handleOnResize)).subscribe(() => {});

    this.subscriptions['settings'] = this.store.select('settings').subscribe((settings: AppSettings) => {
      this.settings = settings;
    });

    this.subscriptions['filters'] = this.store.select('filters').subscribe((filters: AppFilters) => {
      this.filters = filters;

      this.dateSelected = filters.dateSelected;

      this.formatTypeList = filters.formatTypeList!;
      this.formatTypeOption = filters.formatTypeOption;
      this.formatTypeSelected = filters.formatTypeSelected!;
      this.formatTypeApi = filters.formatTypeApi!;
    });

    this.subscriptions['global'] = this.store.select('global').subscribe((global: AppGlobal) => {
      this.global = global;

      this.user = global.user;
      this.collaborator = global.collaborator;
      this.company = global.company;
      this.isSalesforce = global.isSalesforce;

      this.agentDocument = global.document;
      this.agentRole = global.role;

      setTimeout(() => {
        this._isLoading = global.isLoading;
      }, 50);
      this._isSaving = global.isSaving;

      this.isWeb = global.isWeb;
      this.isSmall = global.isSmall;
      this.isPortrait = global.isPortrait;
      this.isMedium = global.isMedium;
      this.isLarge = global.isLarge;

      this.innerHeight = global.innerHeight ?? 0;
      this.innerWidth = global.innerWidth ?? 0;
    });

    this.subscriptions['globalConnected'] = this.store.select('global', 'connected').subscribe((connected: boolean) => {
      this.connected = connected;
      this.networkStatusChanged();
    });

    this.subscriptions['screen'] = this.store.select('screen').subscribe((screen: AppScreen) => {
      this.screen = screen;
      const { claims, screenId } = screen;

      if (!this.ignoreClaims && claims && screenId) {
        const hasLoadClaim = claims[screenId]?.includes('load') ?? false;
        if (!hasLoadClaim) {
          window.location.href = '/p360';
        }
      }
    });
  }

  ngOnDestroy() {
    this.isLoading = false;
    for (const subs in this.subscriptions) {
      this.subscriptions[subs]?.unsubscribe();
    }
  }

  protected onResize = () => {};

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(isLoading: boolean) {
    this._isLoading = isLoading;
    this.store.dispatch(
      appStoreGlobalActions.setIsLoadingAction({
        isLoading,
      }),
    );
  }

  get isInnerLoading() {
    return this._isInnerLoading;
  }

  set isInnerLoading(_isInnerLoading: boolean) {
    this._isInnerLoading = _isInnerLoading;
  }

  get isSaving() {
    return this._isSaving;
  }

  set isSaving(isSaving: boolean) {
    this._isSaving = isSaving;
    this.store.dispatch(
      appStoreGlobalActions.setIsSavingAction({
        isSaving,
      }),
    );
  }

  /** @deprecated */
  handleSaving = (isSaving: boolean = false) => {
    this._isSaving = isSaving;
    this.store.dispatch(
      appStoreGlobalActions.setIsSavingAction({
        isSaving,
      }),
    );
  };

  handleRestErrorMessage = (error: any, showWarning = false, sticky = false) => {
    this.isLoading = false;
    this.service.handleRestErrorMessage(error, showWarning, sticky);
  };

  get unitsFormat(): string | undefined {
    if (!this.settings.format) {
      return undefined;
    }

    return this.settings.format === 'units' ? 'fixedPoint' : this.settings.format;
  }

  get valueFormat(): string | undefined {
    if (!this.settings.format) {
      return undefined;
    }

    return this.settings.format === 'units' ? 'currency' : this.settings.format;
  }

  get collabCode(): number {
    return this.collaborator.agentCode;
  }

  get collabDocument(): string {
    return this.collaborator.document;
  }

  get collabRole(): string {
    return this.collaborator.role;
  }

  protected networkStatusChanged = () => {};

  private handleOnResize = () => {
    const { height, width } = getScreenHeightWithoutTopBar(this.platform);
    this.screenHeight = height;
    this.screenWidth = width;
    this.onResize();
  };
}
